/**
 * `/fly`'s side of the Mission Recorder, as one object.
 *
 * The route needs six things to offer SAVE FLIGHT — a live recorder, a shelf, a
 * suggested name, a prompt flag, a failure message and the name it ended up
 * saved under — and wiring six signals into a route that already owns twenty is
 * how a route stops being readable. They belong together because they change
 * together: every one of them is a consequence of "the pilot pressed save".
 *
 * `createSignal` outside a component is ordinary Solid — the signals belong to
 * whatever owner is current when this is called, which is the route.
 */

import { createSignal, type Accessor } from "solid-js";
import { TAPE_NAME_MAX, type EncounterSetup, type MissionSpec } from "@red-opus/engine";
import { locationCounter } from "./playback-speed.ts";
import { createRecordingSession, subjectFor } from "./recording-session.ts";
import { browserTapeStore, freeTapeName, saveTape } from "./tape-store.ts";

export interface FlightRecording {
  /** Wire straight to `FlightSceneCallbacks.onFlown`. */
  readonly onFlown: ReturnType<typeof createRecordingSession>["onFlown"];
  /** Whether the save prompt is up. */
  readonly prompting: Accessor<boolean>;
  /** The name this flight was saved under, once it has been — the tape list link's target. */
  readonly savedAs: Accessor<string | undefined>;
  readonly error: Accessor<string | undefined>;
  /** A free name to offer, and how long the recording runs — both read at prompt time. */
  readonly suggestedName: () => string;
  readonly duration: () => string;
  readonly openPrompt: () => void;
  readonly closePrompt: () => void;
  readonly save: (name: string) => void;
}

export function createFlightRecording(
  mission: MissionSpec | undefined,
  encounter: EncounterSetup,
  playerId: string,
  title: string,
): FlightRecording {
  const session = createRecordingSession(subjectFor(mission, encounter), playerId, title);
  const store = browserTapeStore();
  const [prompting, setPrompting] = createSignal(false);
  const [savedAs, setSavedAs] = createSignal<string | undefined>(undefined);
  const [error, setError] = createSignal<string | undefined>(undefined);

  return {
    onFlown: session.onFlown,
    prompting,
    savedAs,
    error,
    suggestedName: () => (store ? freeTapeName(store, session.suggestedName(), TAPE_NAME_MAX) : session.suggestedName()),
    duration: () => locationCounter(session.ticks()),
    openPrompt: () => setPrompting(true),
    closePrompt: () => setPrompting(false),
    save: (name) => {
      if (!store) {
        setError("THIS BROWSER HAS NOWHERE TO KEEP A TAPE.");
        return;
      }
      try {
        const tape = session.seal(name);
        saveTape(store, tape);
        setSavedAs(tape.name);
        setPrompting(false);
      } catch (failure) {
        // A full shelf is the expected one, and it is the pilot's to resolve —
        // the message is shown in the prompt so they can delete a tape and try
        // again, rather than losing the recording to a silent refusal.
        setError(failure instanceof Error ? failure.message : String(failure));
      }
    },
  };
}
